import Close from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import { IconButton } from '@src/common/components/IconButton';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { PropsWithChildren, ReactNode } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  titleIcon?: ReactNode;
  marginBottom?: string;
}

export const BaseModal = ({
  open,
  onClose,
  children,
  title,
  subtitle,
  titleIcon,
  marginBottom,
}: PropsWithChildren<Props>) => {
  return (
    <Modal
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          onClose();
        }
      }}
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
    >
      <Stack flex={1} direction="column" className={modalContainer}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
        >
          <Stack
            gap={theme.spacing['1']}
            sx={{ marginBottom: marginBottom ?? theme.spacing['6'] }}
          >
            <Stack
              direction="row"
              gap={theme.spacing['1']}
              sx={{ alignItems: 'center' }}
            >
              {titleIcon}
              <Typography id="modal-title" component="h2" variant="h5">
                {title}
              </Typography>
            </Stack>
            {subtitle && (
              <Typography
                id="modal-title"
                variant="body1"
                style={{ color: theme.palette.surface.onSurfaceVariant }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>

          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack className={modalContent}>{children}</Stack>
      </Stack>
    </Modal>
  );
};

const scrollbarPadding = '28px';

const modalContainer = css({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.surface.surface,
  width: 800,
  borderRadius: 28,
  padding: scrollbarPadding,
  overflow: 'hidden',
});

const modalContent = css({
  paddingLeft: scrollbarPadding,
  paddingRight: scrollbarPadding,
  paddingTop: theme.spacing['3'],
  maxHeight: '80vh',
  overflowY: 'auto',
  margin: `0px -${scrollbarPadding}`,
});
